import { Button, Typography } from '@mui/material'
import React, { FC, forwardRef, useState } from 'react'
import {
  InformationModal,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import { ArtistCreatePostCategoryInput } from '@/clients/utoniq-core/schema'
import { tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame } from '@/core/architecture'
import { captureError } from '@/core/errors'
import { Form, handleError, useForm, UseFormProps } from '@/core/form'

export type PostCategoryCreateValues = {
  name: string
  description: string
}

export type CreatePostCategoryModalProps = {
  open: boolean
  onClose: () => void
  onCreateCategory: (inputs: ArtistCreatePostCategoryInput) => Promise<void>
}

const schema: UseFormProps['schema'] = o => ({
  name: o.string().max(20).label(tLabel('consoleCreateCategory.name')),
  description: o
    .string()
    .max(10000)
    .label(tLabel('consoleCreateCategory.description')),
})

const defaultValues: PostCategoryCreateValues = {
  name: '',
  description: '',
}

export const CreatePostCategoryModal: FC<CreatePostCategoryModalProps> = ({
  open,
  onClose,
  onCreateCategory,
}) => {
  const { t } = useTranslation('consolePostCategories')
  const { showMessage, showError } = useMessage()
  const [loading, setLoading] = useState(false)
  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setError,
    getValues,
    reset,
  } = useForm<PostCategoryCreateValues>({
    schema,
    defaultValues: {
      ...defaultValues,
    },
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onCreatePostCategory = async () => {
    setLoading(true)
    const { ...inputs } = getValues()
    return onCreateCategory({ ...inputs })
      .then(() => {
        showMessage(t('completePostCreate'))
        handleClose()
      })
      .catch(e => {
        captureError(e, 'consoleCategoryCreate')
        handleError<PostCategoryCreateValues>(e, setError, showError)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <InformationModal
      open={open}
      onClose={() => handleClose()}
      innerSx={{
        width: '80vw',
        maxWidth: 500,
      }}
      title={t('createCategoryModalTitle')}
      content={
        <Form onSubmit={handleSubmit(onCreatePostCategory)} sx={{ pt: 6 }}>
          <TextInputCell
            type="text"
            textFieldProps={{
              variant: 'standard',
              InputProps: {
                sx: {
                  bgcolor: 'background.default',
                  px: 4,
                  py: 3,
                },
                disableUnderline: true,
              },
            }}
            titleProps={{
              variant: 'h5',
              color: 'text.primary',
              textAlign: 'left',
            }}
            error={errors.name}
            {...register('name')}
          />
          <TextInputCell
            type="textarea"
            textFieldProps={{
              variant: 'standard',
              multiline: true,
              rows: 5,
              InputProps: {
                sx: {
                  bgcolor: 'background.default',
                  px: 4,
                  py: 3,
                },
                disableUnderline: true,
              },
            }}
            titleProps={{
              children: (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'text.primary',
                      textAlign: 'left',
                    }}
                  >
                    {t('descriptionTitle')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'left',
                    }}
                  >
                    {t('descriptionDetail')}
                  </Typography>
                </>
              ),
            }}
            error={errors.description}
            {...register('description')}
          />
          <Button
            type="submit"
            size="large"
            color="primary"
            sx={{ py: 4, mb: 5, minWidth: 212 }}
            onClick={() => handleSubmit(onCreatePostCategory)}
            loading={loading}
            disabled={loading}
          >
            {t('createCategory')}
          </Button>
        </Form>
      }
      cancelText={t('cancel')}
    />
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePostCategories')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)
